import React, { useCallback, useEffect, useMemo, useState } from 'react';
import http from '../services/api';
import { toast } from '../hooks/use-toast';
import { Loader2, RefreshCw, DollarSign, Search, CreditCard, Download, Gift, Users } from 'lucide-react';

const METHOD_STYLES = {
  stripe:  { label: 'Stripe',  cls: 'bg-indigo-500/15 border-indigo-500/40 text-indigo-300' },
  paypal:  { label: 'PayPal',  cls: 'bg-blue-500/15 border-blue-500/40 text-blue-300' },
  comp:    { label: 'Comp',    cls: 'bg-amber-500/15 border-amber-500/40 text-amber-300' },
};

const STATUS_STYLES = {
  active:    'bg-emerald-500/15 border-emerald-500/40 text-emerald-300',
  completed: 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300',
  paid:      'bg-emerald-500/15 border-emerald-500/40 text-emerald-300',
  pending:   'bg-amber-500/15 border-amber-500/40 text-amber-300',
  canceled:  'bg-neutral-600/20 border-neutral-500/40 text-neutral-300',
  expired:   'bg-red-500/15 border-red-500/40 text-red-300',
  unassigned:'bg-blue-500/15 border-blue-500/40 text-blue-300',
};

const fmtMoney = (n) => (n == null ? '—' : `$${Number(n).toFixed(2)}`);
const fmtDate  = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

export default function AdminSalesPanel() {
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ count: 0, stripeCount: 0, paypalCount: 0, revenue: 0 });
  const [loading, setLoading] = useState(true);
  const [method, setMethod] = useState('all');
  const [status, setStatus] = useState('all');
  const [q, setQ] = useState('');
  const [lastRefresh, setLastRefresh] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (method !== 'all')  params.method = method;
      if (status !== 'all')  params.status = status;
      if (q) params.q = q;
      const r = await http.get('/admin/sales', { params }).then(r => r.data);
      setRows(r.rows || []);
      setTotals(r.totals || {});
      setLastRefresh(new Date());
    } catch (err) {
      toast({ title: 'Load failed', description: err?.response?.data?.detail || 'Try again.' });
    } finally { setLoading(false); }
  }, [method, status, q]);

  // Real-time: refetch every 30s so new Stripe/PayPal captures surface automatically.
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const exportCsv = () => {
    const headers = ['Date', 'Method', 'Business', 'Member', 'Email', 'Tier', 'Amount', 'Status', 'External ID'];
    const lines = [headers.join(',')];
    for (const r of rows) {
      const cols = [
        r.createdAt || '',
        METHOD_STYLES[r.method]?.label || r.method,
        (r.businessName || '').replace(/"/g, '""'),
        (r.memberName || '').replace(/"/g, '""'),
        r.email || '',
        (r.tierLabel || r.package || '').replace(/"/g, '""'),
        r.amount || 0,
        r.status || '',
        r.externalId || '',
      ];
      lines.push(cols.map(c => `"${c}"`).join(','));
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pbb-sales-${new Date().toISOString().slice(0,16).replace(/[:T]/g,'-')}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const filteredChip = useMemo(() => {
    const bits = [];
    if (method !== 'all') bits.push(METHOD_STYLES[method]?.label || method);
    if (status !== 'all') bits.push(status);
    if (q) bits.push(`"${q}"`);
    return bits.join(' · ') || null;
  }, [method, status, q]);

  return (
    <div className="space-y-4" data-testid="admin-sales-panel">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-black text-xl text-white flex items-center gap-2">
            <DollarSign size={18} className="text-emerald-400"/> Sales Tracking
          </h2>
          <p className="text-xs text-gray-500">
            Unified real-time ledger — every Stripe and PayPal subscription payment,
            with the business, member, tier, amount, date and current status.
            Auto-refreshes every 30 seconds.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} disabled={loading}
            data-testid="admin-sales-refresh"
            className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border border-neutral-700 hover:bg-neutral-800">
            {loading ? <Loader2 size={12} className="animate-spin"/> : <RefreshCw size={12}/>} Refresh
          </button>
          <button onClick={exportCsv} disabled={!rows.length}
            data-testid="admin-sales-export"
            className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-emerald-600 hover:bg-emerald-500 text-black font-semibold disabled:opacity-50">
            <Download size={12}/> Export CSV
          </button>
        </div>
      </div>

      {/* Stat chips */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Chip icon={DollarSign} tint="emerald" label="Total revenue" value={fmtMoney(totals.revenue)} testId="chip-revenue"/>
        <Chip icon={CreditCard} tint="indigo" label="Stripe payments" value={totals.stripeCount} testId="chip-stripe"/>
        <Chip icon={CreditCard} tint="blue" label="PayPal payments" value={totals.paypalCount} testId="chip-paypal"/>
        <Chip icon={Gift} tint="amber" label="Comp'd" value={totals.compCount} testId="chip-comp"/>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"/>
          <input value={q} onChange={e => setQ(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') load(); }}
            placeholder="Search by business, member, or email…"
            data-testid="admin-sales-search"
            className="w-full bg-black border border-[#1f1f1f] rounded-lg pl-9 pr-3 py-2 text-sm text-white"/>
        </div>
        <SegmentedToggle label="Method" value={method} onChange={setMethod}
          testIdPrefix="admin-sales-method"
          options={[['all','All'], ['stripe','Stripe'], ['paypal','PayPal'], ['comp','Comp']]}/>
        <SegmentedToggle label="Status" value={status} onChange={setStatus}
          testIdPrefix="admin-sales-status"
          options={[['all','All'], ['active','Active'], ['completed','Completed'], ['canceled','Canceled']]}/>
        {filteredChip && <div className="text-[10px] text-gray-400">Filter: {filteredChip}</div>}
      </div>

      <div className="rounded-xl border border-[#1f1f1f] bg-[#0f0f0f] overflow-hidden">
        {loading && rows.length === 0 ? (
          <div className="p-10 text-center text-gray-500"><Loader2 className="animate-spin inline mr-2" size={14}/> Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-gray-500 text-sm">No payments match these filters.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-gray-500 bg-black/40 text-left">
                <tr>
                  <th className="px-3 py-2">Date</th>
                  <th className="px-3 py-2">Method</th>
                  <th className="px-3 py-2">Business</th>
                  <th className="px-3 py-2">Member</th>
                  <th className="px-3 py-2">Tier</th>
                  <th className="px-3 py-2 text-right">Amount</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Ref</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={`${r.method}-${r.id}`} className="border-t border-[#1a1a1a] hover:bg-neutral-900/40"
                    data-testid={`admin-sales-row-${r.id}`}>
                    <td className="px-3 py-2 text-gray-400 whitespace-nowrap">{fmtDate(r.createdAt)}</td>
                    <td className="px-3 py-2">
                      <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border inline-flex items-center gap-1 ${METHOD_STYLES[r.method]?.cls || ''}`}
                        data-testid={`admin-sales-method-${r.id}`}>
                        {METHOD_STYLES[r.method]?.label || r.method}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-white">{r.businessName || <span className="text-gray-600 italic">—</span>}</td>
                    <td className="px-3 py-2 text-white">
                      <div>{r.memberName || <span className="text-gray-600 italic">—</span>}</div>
                      {r.email && <div className="text-[10px] text-gray-500">{r.email}</div>}
                    </td>
                    <td className="px-3 py-2 text-gray-300">{r.tierLabel || r.package || '—'}</td>
                    <td className="px-3 py-2 text-right font-bold text-emerald-300">{fmtMoney(r.amount)}</td>
                    <td className="px-3 py-2">
                      <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${STATUS_STYLES[r.status] || 'bg-neutral-500/10 border-neutral-500/30 text-neutral-300'}`}>
                        {r.status || '—'}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-gray-500 font-mono text-[10px] max-w-[180px] truncate" title={r.externalId}>
                      {r.externalId || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="text-[10px] text-gray-500 text-right">
        {lastRefresh && <>Last refreshed {lastRefresh.toLocaleTimeString()} · {rows.length} rows shown</>}
      </div>
    </div>
  );
}

function Chip({ icon: Icon, tint, label, value, testId }) {
  const tintMap = {
    emerald: 'from-emerald-500/15 to-emerald-500/5 border-emerald-500/30 text-emerald-200',
    indigo:  'from-indigo-500/15 to-indigo-500/5 border-indigo-500/30 text-indigo-200',
    blue:    'from-blue-500/15 to-blue-500/5 border-blue-500/30 text-blue-200',
    amber:   'from-amber-500/15 to-amber-500/5 border-amber-500/30 text-amber-200',
  };
  return (
    <div className={`rounded-xl border bg-gradient-to-br p-3 ${tintMap[tint] || ''}`} data-testid={testId}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider opacity-80"><Icon size={12}/> {label}</div>
      <div className="text-2xl font-black mt-1">{value}</div>
    </div>
  );
}

function SegmentedToggle({ label, value, onChange, options, testIdPrefix }) {
  return (
    <div className="inline-flex items-center gap-1.5">
      <span className="text-[10px] uppercase tracking-wider text-gray-500">{label}</span>
      <div className="inline-flex rounded-full border border-neutral-700 overflow-hidden text-xs">
        {options.map(([key, lbl]) => (
          <button key={key} onClick={() => onChange(key)}
            data-testid={`${testIdPrefix}-${key}`}
            className={`px-2.5 py-1 ${value === key ? 'bg-emerald-500 text-black font-semibold' : 'text-gray-400 hover:bg-neutral-800'}`}>
            {lbl}
          </button>
        ))}
      </div>
    </div>
  );
}
